import { useFormContext } from 'react-hook-form';
import styled, { keyframes } from 'styled-components';
import { caption, colors, duration, easing, fadeIn, spacing, subtleShadow } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { PROMPT_FIELD } from './JourneyAssist.constants';
import { getPromptValue } from './utils/PromptUtils';

const fadeOut = keyframes`
  from {
    opacity: 1;
  } 
  to {
    opacity: 0;
    display: none;
  }
`;

const StyledButtons = styled.div<{ shouldShow?: boolean }>`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: ${spacing.half};
  gap: ${spacing.half};
  display: flex;
  flex-wrap: wrap;
  opacity: ${({ shouldShow }) => (shouldShow ? 0 : 1)};
  animation: ${duration.long} ${({ shouldShow }) => (shouldShow ? fadeIn : fadeOut)}
    ${duration.medium} ${easing.standard} forwards;
`;

const StyledButton = styled.button`
  ${caption}
  ${subtleShadow}
  border-radius: ${pxToRem(1000)};
  border: 1px solid ${colors.purple20};
  background: ${colors.white};
  color: ${colors.purple90};
  padding: ${spacing.threeEights} ${spacing.threeQuarter};

  &:hover {
    border: 1px solid #d3a8dd;
  }
`;

type Preset = {
  buttonText: string;
  value: string;
};

export const PILLS_TEST_ID = 'helper-pills';
export const HELPER_PILL_TEST_ID = 'helper-pill';

export const HelperPills = ({
  inputRef = null,
  shouldAddToPrompt,
  shouldShow,
  pills,
}: {
  inputRef?: React.RefObject<HTMLTextAreaElement> | null;
  shouldAddToPrompt?: boolean;
  shouldShow: boolean;
  pills: Preset[];
}) => {
  const { setValue, watch } = useFormContext();
  const [prompt] = watch([PROMPT_FIELD]);

  const handleClick = (value: string) => {
    setValue(PROMPT_FIELD, getPromptValue(prompt, value, shouldAddToPrompt));
    inputRef?.current?.focus();
  };

  return (
    <StyledButtons shouldShow={shouldShow} data-test={PILLS_TEST_ID}>
      {pills.map((pill) => (
        <StyledButton
          key={pill.buttonText}
          onClick={() => handleClick(pill.value)}
          data-test={HELPER_PILL_TEST_ID}
        >
          {pill.buttonText}
        </StyledButton>
      ))}
    </StyledButtons>
  );
};
